import { useState, useEffect, useCallback } from "react";
import { Loader2, Download, AlertCircle, ShoppingCart, RotateCw } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import DataSourceNotice from "@/components/DataSourceNotice";

import PurchasingKPIDashboard from "@/components/purchasing/PurchasingKPIDashboard";
import PurchaseValueAnalytics from "@/components/purchasing/PurchaseValueAnalytics";
import POTable from "@/components/purchasing/POTable";
import VendorAnalysis from "@/components/purchasing/VendorAnalysis";
import PurchasingChat from "@/components/purchasing/PurchasingChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PurchasingAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const res = await axios.get(`${API}/purchasing-agent/data`, {
        params: { period_days: 90 },
      });
      setData(res.data);
      if (isRefresh) {
        toast.success("Purchasing data refreshed");
      }
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Access denied. Purchasing Analyst role required.");
      } else {
        toast.error("Failed to load purchasing data");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(false); }, [fetchData]);

  const handleExport = async (reportType) => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/purchasing-agent/export/${reportType}`, {
        params: { period_days: 90 },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `purchasing_${reportType}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="purchasing-analytics-page">
      <DataSourceNotice source={data?.data_source} message={data?.message} />
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingCart className="w-6 h-6 text-violet-600" />
            Purchasing Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Procurement performance, vendor management, and AI-powered spend insights
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {data?.data_source === "mock" && (
            <Badge variant="secondary" className="gap-1 text-amber-700 bg-amber-50 border-amber-200" data-testid="pu-mock-data-badge">
              <AlertCircle className="w-3 h-3" /> Sample Data
            </Badge>
          )}
          <Button
            size="sm"
            className="h-8 px-2.5 shrink-0 justify-center text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs gap-1.5"
            onClick={() => fetchData(true)}
            disabled={loading || refreshing}
            data-testid="pu-refresh-btn"
          >
            <RotateCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="w-full justify-start" data-testid="pu-tabs">
          <TabsTrigger value="dashboard" data-testid="pu-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="purchase_value" data-testid="pu-tab-purchase-value">Purchase Value</TabsTrigger>
          <TabsTrigger value="orders" data-testid="pu-tab-orders">Purchase Orders</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="pu-tab-vendors">Vendor Analysis</TabsTrigger>
          <TabsTrigger value="analyst" data-testid="pu-tab-analyst">AI Analyst</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4">
          <PurchasingKPIDashboard kpis={data?.kpis} data={data} />
        </TabsContent>

        <TabsContent value="purchase_value" className="mt-4">
          <PurchaseValueAnalytics data={data} onExport={handleExport} />
        </TabsContent>

        <TabsContent value="orders" className="mt-4">
          <POTable data={data?.purchase_orders} suppliers={data?.suppliers} onExport={handleExport} exporting={exporting} />
        </TabsContent>

        <TabsContent value="vendors" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button variant="outline" size="sm" onClick={() => handleExport("vendors")} disabled={exporting} data-testid="pu-export-vendors-btn">
              <Download className="w-4 h-4 mr-1" /> Export Vendors
            </Button>
          </div>
          <VendorAnalysis vendors={data?.vendor_performance} spendByCategory={data?.spend_by_category} />
        </TabsContent>

        <TabsContent value="analyst" className="mt-4">
          <PurchasingChat />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PurchasingAnalytics;
